import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { EDR_COLORS, edrCardSx } from './edrTokens';

const ApexChart = Chart as React.ComponentType<any>;

const CATEGORY_PALETTE = [
  EDR_COLORS.critical,
  '#C05621',
  EDR_COLORS.high,
  EDR_COLORS.brand500,
  '#805AD5',
  EDR_COLORS.medium,
  '#0891B2',
  EDR_COLORS.slate500,
];

type EdrMalwareChartsProps = {
  categories?: Record<string, number> | null;
};

const toTitle = (value: string): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/** Case-insensitive merge so Active/active (etc.) do not double-count. */
const mergeCounts = (raw?: Record<string, number> | null): [string, number][] => {
  const merged = new Map<string, number>();
  Object.entries(raw ?? {}).forEach(([key, count]) => {
    if (typeof count !== 'number' || count <= 0) return;
    const normalized = key.trim().toLowerCase();
    if (!normalized) return;
    merged.set(normalized, (merged.get(normalized) ?? 0) + count);
  });
  return Array.from(merged.entries());
};

const normalizeCategories = (raw?: Record<string, number> | null) => {
  const entries = mergeCounts(raw).sort((a, b) => b[1] - a[1]);
  const labels = entries.map(([key]) => toTitle(key));
  const series = entries.map(([, count]) => count);
  const total = series.reduce((sum, count) => sum + count, 0);
  return { labels, series, total };
};

export const EdrMalwareCharts: FC<EdrMalwareChartsProps> = ({ categories }) => {
  const { labels, series, total: categoriesTotal } = useMemo(
    () => normalizeCategories(categories),
    [categories]
  );

  const donutOptions = useMemo(
    () => ({
      chart: {
        type: 'donut' as const,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      labels,
      colors: labels.map((_, index) => CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]),
      legend: {
        show: true,
        position: 'right' as const,
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        labels: { colors: EDR_COLORS.textBody },
        markers: { width: 8, height: 8, radius: 4 },
        itemMargin: { vertical: 4 },
        formatter: (seriesName: string, opts?: { seriesIndex?: number }) => {
          const index = opts?.seriesIndex ?? labels.indexOf(seriesName);
          const value = series[index] ?? 0;
          const pct = categoriesTotal > 0 ? Math.round((value / categoriesTotal) * 100) : 0;
          return `${seriesName}: ${value.toLocaleString()} (${pct}%)`;
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '68%',
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: '20px',
                fontWeight: 700,
                color: EDR_COLORS.textPrimary,
                fontFamily: 'Inter, sans-serif',
                formatter: () => categoriesTotal.toLocaleString(),
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Threats',
                fontSize: '10px',
                color: EDR_COLORS.textMuted,
                fontFamily: 'Inter, sans-serif',
                formatter: () => categoriesTotal.toLocaleString(),
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      tooltip: {
        enabled: true,
        theme: 'light' as const,
        y: {
          formatter: (value: number) => {
            const pct = categoriesTotal > 0 ? Math.round((value / categoriesTotal) * 100) : 0;
            return `${value.toLocaleString()} (${pct}%)`;
          },
        },
      },
      states: {
        hover: { filter: { type: 'none' as const } },
        active: { filter: { type: 'none' as const } },
      },
    }),
    [labels, series, categoriesTotal]
  );

  return (
    <Card sx={{ ...edrCardSx, height: '100%' }}>
      <CardContent style={{ padding: '14px 16px', height: '100%', boxSizing: 'border-box' }}>
        <Typography
          style={{ fontSize: 12, fontWeight: 600, color: EDR_COLORS.textHeading, marginBottom: 8 }}
        >
          Malware Categories
        </Typography>
        {series.length ? (
          <ApexChart options={donutOptions} series={series} type="donut" height={260} />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ height: 260, color: EDR_COLORS.textMuted, fontSize: 12 }}
          >
            No category data
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
